import { Color, Spacing } from '@persona-id/design-tokens';
import { useState } from 'react';
import styled from 'styled-components';

import {
  CheckMetadataItem,
  CheckMetadataItemType,
  DynamicTableCheckMetadataItem,
  formatMetadataValue,
  TableCheckMetadataItem,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { Check, CheckStatus } from 'dashboard/models/check';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';

export interface ChecksTableRowMetadataProps {
  id: string;
  check?: Check; // Note: No check is passed in if we are rendering metadata for a group of checks
  metadata: CheckMetadataItem[];
  hideIfVerificationPassed?: boolean;
  showReasonsIfVerificationFailed?: boolean;
  showReasonsIfVerificationNotApplicable?: boolean;
}

const ChecksTableRowMetadata = ({ id, check, metadata }: ChecksTableRowMetadataProps) => {
  return (
    <CheckMetadataWrapper>
      {check && <CheckMetadataReasons check={check} />}
      {metadata.length > 0 && <CheckMetadataHeader>Details</CheckMetadataHeader>}
      {metadata.map((metadataItem, index) => {
        switch (metadataItem.type) {
          case CheckMetadataItemType.Simple:
            return (
              <CheckMetadataTable>
                <TableBody>
                  <TableRow
                    key={`checks-table-v2__row-${id}-metadata-row-${index}`}
                    disableHoverBackground={true}
                  >
                    <CheckMetadataTableCell>
                      {metadataItem.label}
                      {metadataItem.labelHelpText && (
                        <HelpIcon>{metadataItem.labelHelpText}</HelpIcon>
                      )}
                    </CheckMetadataTableCell>
                    <CheckMetadataTableCell>
                      {formatMetadataValue(metadataItem.value)}
                    </CheckMetadataTableCell>
                  </TableRow>
                </TableBody>
              </CheckMetadataTable>
            );
          case CheckMetadataItemType.Group:
            return (
              <CheckMetadataTable>
                <TableBody>
                  <TableRow
                    key={`checks-table-v2__row-${id}-metadata-row-${index}`}
                    disableHoverBackground={true}
                  >
                    <CheckMetadataTableCell colSpan={2}>
                      <CheckMetadataGroupHeader>
                        {metadataItem.heading.title}
                        {metadataItem.heading.sticker}
                      </CheckMetadataGroupHeader>
                      <CheckMetadataGroupTable>
                        <TableBody>
                          {metadataItem.items.map((item, itemIndex) => (
                            <TableRow
                              key={`checks-table-v2__row-${id}-metadata-row-${index}-item-${itemIndex}`}
                              disableHoverBackground={true}
                            >
                              <CheckMetadataTableCell>
                                {item.label}
                                {item.labelHelpText && <HelpIcon>{item.labelHelpText}</HelpIcon>}
                              </CheckMetadataTableCell>
                              <CheckMetadataTableCell>
                                {formatMetadataValue(item.value)}
                              </CheckMetadataTableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </CheckMetadataGroupTable>
                    </CheckMetadataTableCell>
                  </TableRow>
                </TableBody>
              </CheckMetadataTable>
            );
          case CheckMetadataItemType.Table:
            return (
              <TableWrapper>
                <CheckMetadataGroupHeader>
                  {metadataItem.heading.title}
                  {metadataItem.heading.sticker}
                </CheckMetadataGroupHeader>
                <Spacer height={1} />
                {metadataItem.subheading && (
                  <>
                    <CheckMetadataGroupSubheader>
                      {metadataItem.subheading}
                    </CheckMetadataGroupSubheader>
                    <Spacer height={2} />
                  </>
                )}
                <CheckMetadataDataTable metadataItem={metadataItem} id={id} index={index} />
              </TableWrapper>
            );
          case CheckMetadataItemType.DynamicTable:
            return <CheckMetadataDynamicTable metadataItem={metadataItem} id={id} index={index} />;
          default:
            return null;
        }
      })}
    </CheckMetadataWrapper>
  );
};

const CheckMetadataReasons = ({ check }: { check: Check }) => {
  return (
    <>
      {check.reasons?.length === 1 &&
        CHECK_DISPLAY_CONFIG[check.name].reasons[check.reasons[0]] && (
          <>
            <CheckMetadataHeader>
              {check.status === CheckStatus.Passed
                ? 'Success Reason'
                : check.status === CheckStatus.Failed
                  ? 'Failure Reason'
                  : 'Reason'}
            </CheckMetadataHeader>
            <CheckMetadataReasonsText color='primary'>
              {CHECK_DISPLAY_CONFIG[check.name].reasons[check.reasons[0]]}
            </CheckMetadataReasonsText>
          </>
        )}
      {check.reasons && check.reasons.length > 1 && (
        <>
          <CheckMetadataHeader>
            {check.status === CheckStatus.Passed
              ? 'Success Reasons'
              : check.status === CheckStatus.Failed
                ? 'Failure Reasons'
                : 'Reasons'}
          </CheckMetadataHeader>
          <CheckMetadataReasonsText color='primary'>
            <UnorderedList>
              {check.reasons.map((reason, index) => (
                <li key={`checks-table-v2__row-${check.name}-metadata-reason-${index}`}>
                  {CHECK_DISPLAY_CONFIG[check.name].reasons[reason]}
                </li>
              ))}
            </UnorderedList>
          </CheckMetadataReasonsText>
        </>
      )}
      {/* For now, we display "This check was not run" for required N/A checks that do not have other reasons,
      or do not have display text for their reason */}
      {check.status === CheckStatus.NotApplicable &&
        (!check.reasons ||
          check.reasons.length === 0 ||
          !CHECK_DISPLAY_CONFIG[check.name].reasons[check.reasons[0]]) && (
          <>
            <CheckMetadataHeader>Reason</CheckMetadataHeader>
            <CheckMetadataReasonsText color='primary'>
              This check was not run.
            </CheckMetadataReasonsText>
          </>
        )}
    </>
  );
};

interface CheckMetadataDataTableProps {
  metadataItem: TableCheckMetadataItem;
  id: string;
  index: number;
}

const CheckMetadataDataTable = ({ metadataItem, id, index }: CheckMetadataDataTableProps) => {
  return (
    <Table>
      <TableHead>
        {metadataItem.rowHeaders.map((cell, cellIndex) => (
          <TableHeaderCell
            key={`checks-table-v2__row-${id}-metadata-${index}-header-cell-${cellIndex}`}
          >
            {cell}
          </TableHeaderCell>
        ))}
      </TableHead>
      <TableBody>
        {(metadataItem.rows.length === 0 ||
          (metadataItem.rows.length === 1 && metadataItem.rows[0].length === 0)) &&
        metadataItem.tableEmptyState ? (
          <TableRow disableHoverBackground={true}>
            <TableCell colSpan={metadataItem.rowHeaders.length}>
              {metadataItem.tableEmptyState}
            </TableCell>
          </TableRow>
        ) : (
          metadataItem.rows.map((row, rowIndex) => (
            <TableRow
              key={`checks-table-v2__row-${id}-metadata-${index}-row=${rowIndex}`}
              disableHoverBackground={true}
            >
              {row.map((cell, cellIndex) => (
                <TableCell
                  key={`checks-table-v2__row-${id}-metadata-${index}-row=${rowIndex}-cell-${cellIndex}`}
                >
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
};

interface CheckMetadataDynamicTableProps {
  metadataItem: DynamicTableCheckMetadataItem;
  id: string;
  index: number;
}

const CheckMetadataDynamicTable = ({ metadataItem, id, index }: CheckMetadataDynamicTableProps) => {
  const [selectedDynamicTableOption, setSelectedDynamicTableOption] = useState(
    metadataItem.options[0].value,
  );

  return (
    <TableWrapper>
      <CheckMetadataGroupHeader>
        {metadataItem.heading.title}
        {metadataItem.heading.sticker}
      </CheckMetadataGroupHeader>
      <Spacer height={1} />
      <DynamicTableSelect
        defaultValue={selectedDynamicTableOption}
        onValueChange={(value: string) => setSelectedDynamicTableOption(value)}
      >
        {metadataItem.options.map((option, optionIndex) => (
          <Option
            key={`checks-table-v2__row-${id}-metadata-option-${optionIndex}`}
            value={option.value}
          >
            <DynamicTableOption>{option.label}</DynamicTableOption>
          </Option>
        ))}
      </DynamicTableSelect>
      <Spacer height={2} />
      <CheckMetadataDataTable
        metadataItem={
          {
            ...(() => {
              // Remove `options` and update `rows` to convert from a DynamicTableCheckMetadataItem to a TableCheckMetadataItem
              const { options, ...rest } = metadataItem;
              return {
                ...rest,
                rows: metadataItem.rows[selectedDynamicTableOption],
                type: CheckMetadataItemType.Table,
              };
            })(),
          } as TableCheckMetadataItem
        }
        id={id}
        index={index}
      />
    </TableWrapper>
  );
};

const checkMetadataWrapperMarginLeft = '14px';

const CheckMetadataWrapper = styled.div`
  // Note: Spacing is not used here so that we can achieve exact alignment with the Status sticker
  margin-left: ${checkMetadataWrapperMarginLeft};
`;

const CheckMetadataHeader = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-left: ${Spacing[3]};
  margin-bottom: ${Spacing[2]};
`;

const CheckMetadataReasonsText = styled(Paragraph)`
  margin-bottom: ${Spacing[4]};
  margin-left: ${Spacing[3]};
`;

const CheckMetadataTable = styled(Table)`
  table-layout: auto;
  width: auto;
`;

const CheckMetadataGroupHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[2]};
  color: ${Color.TextPrimary};
  font-weight: bold;
  margin-bottom: ${Spacing[2]};
`;

const CheckMetadataGroupSubheader = styled.div`
  line-height: 1.5;
`;

const CheckMetadataGroupTable = styled(Table)`
  margin-left: -${Spacing[3]};
`;

const CheckMetadataTableCell = styled(TableCell)`
  vertical-align: top;
  border-bottom: 0px;
  max-width: 500px;
  white-space: normal;
  line-height: 1.5;

  :nth-child(1) {
    color: ${Color.TextSecondary};
  }
`;

const TableWrapper = styled.div`
  margin-left: ${Spacing[3]};
  margin-top: ${Spacing[4]};

  // Styles the table for CheckMetadataItemType.Table
  table td {
    max-width: 300px;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.5;
  }
`;

const UnorderedList = styled.ul`
  margin-left: ${Spacing[4]};
`;

const DynamicTableSelect = styled(Select)`
  width: auto;
`;

const DynamicTableOption = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[2]};
`;

export default ChecksTableRowMetadata;
